import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import { ServiceUrlConstants } from '../../constants/service-url-constants'
import { DocumentService } from '../../services/common/document.service'
import { CommonService, convertISOToDate } from '../../services/common/common.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { Subscription } from 'rxjs'
import { AppState } from 'src/app/store/state/app.state'
import { getEnabledModules } from 'src/app/store/selector/company-settings.selector'
import { RelatedCompaniesAPIModel } from 'src/app/models/api.model'

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  protected readonly ServiceUrlConstants = ServiceUrlConstants

  @ViewChild('companyDropdownRef') companyDropdownRef: ElementRef

  activeTab: 'standard' | 'overall' = 'standard'

  selectedReport: any = null
  selectedOverallReport: any = null

  relatedCompanies: RelatedCompaniesAPIModel = null
  allCompanyOptions: { value: string; display: string; type: 'group' | 'entity' }[] = []
  selectedCompanyIds: Set<string> = new Set()
  showCompanyDropdown = false
  companySearchText = ''

  private readonly allReports = [
    {
      label: 'All Queries Compliance Report',
      type: ServiceUrlConstants.API_REPORT_TYPE_ALL_QUERIES_COMPLIANCE_REPORT,
      requiredModule: 'QUERIES',
      filters: [
        { label: 'From', apiParam: 'fromDate', type: 'date', value: null },
        { label: 'To', apiParam: 'toDate', type: 'date', value: null }
      ]
    },
    {
      label: 'Feedback Summary',
      type: ServiceUrlConstants.API_REPORT_TYPE_FEEDBACK_SUMMARY,
      requiredModule: null,
      filters: [
        { label: 'From', apiParam: 'fromDate', type: 'date', value: null },
        { label: 'To', apiParam: 'toDate', type: 'date', value: null }
      ]
    },
    {
      label: 'New Joinee Workflow',
      type: ServiceUrlConstants.API_REPORT_TYPE_NEW_JOINEE_WORKFLOW,
      requiredModule: 'ACTIVITY_TRACKER',
      filters: [
        { label: 'From', apiParam: 'fromDate', type: 'date', value: null },
        { label: 'To', apiParam: 'toDate', type: 'date', value: null },
        { label: 'Company Entities list', apiParam: 'companyEntityIds', type: 'multiselect', options: [], value: [] }
      ]
    }
  ]

  private readonly allOverallReports = [
    {
      label: 'Activity Board Overall',
      type: ServiceUrlConstants.API_REPORT_TYPE_ACTIVITY_BOARD_OVERALL,
      requiredModule: 'ACTIVITY_TRACKER',
      filters: [
        { label: 'From', apiParam: 'fromDate', type: 'date', value: null },
        { label: 'To', apiParam: 'toDate', type: 'date', value: null }
      ]
    },
    {
      label: 'GB Task Overall',
      type: ServiceUrlConstants.API_REPORT_TYPE_GB_TASK_OVERALL,
      requiredModule: 'GOVERNMENT_BUSINESS_TASK',
      filters: [
        { label: 'From', apiParam: 'fromDate', type: 'date', value: null },
        { label: 'To', apiParam: 'toDate', type: 'date', value: null }
      ]
    },
    {
      label: 'Query Overall',
      type: ServiceUrlConstants.API_REPORT_TYPE_QUERY_OVERALL,
      requiredModule: 'QUERIES',
      filters: [
        { label: 'From', apiParam: 'fromDate', type: 'date', value: null },
        { label: 'To', apiParam: 'toDate', type: 'date', value: null }
      ]
    }
  ]

  reports = [...this.allReports]
  overallReports = [...this.allOverallReports]

  relatedCompaniesSubscription$: Subscription
  relatedCompaniesFullSubscription$: Subscription
  enabledModulesSubscription$: Subscription

  constructor(
    private documentService: DocumentService,
    private cs: CommonService,
    private initializeService: InitializeService,
    private store: Store<AppState>
  ) {
    this.selectedReport = this.allReports[0]
    this.selectedOverallReport = this.allOverallReports[0]
  }

  ngOnInit(): void {
    this.enabledModulesSubscription$ = this.store.select(getEnabledModules).subscribe(enabledModules => {
      this.reports = this.allReports.filter(
        report => report.requiredModule === null || enabledModules.includes(report.requiredModule)
      )
      this.selectedReport = this.reports[0] ?? null

      this.overallReports = this.allOverallReports.filter(
        report => report.requiredModule === null || enabledModules.includes(report.requiredModule)
      )
      this.selectedOverallReport = this.overallReports[0] ?? null
    })

    this.getSelectedCompanies()
    this.getRelatedCompanies()
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (
      this.showCompanyDropdown &&
      this.companyDropdownRef &&
      !this.companyDropdownRef.nativeElement.contains(event.target)
    ) {
      this.showCompanyDropdown = false
      this.companySearchText = ''
    }
  }

  private getRelatedCompanies() {
    this.relatedCompaniesFullSubscription$ = this.initializeService.getRelatedCompanies().subscribe(relatedCompanies => {
      if (!relatedCompanies) return
      this.relatedCompanies = relatedCompanies
      const groupOptions = (relatedCompanies.group ?? []).map(g => ({ value: g.id, display: g.name, type: 'group' as const }))
      const entityOptions = (relatedCompanies.entity ?? []).map(e => ({ value: e.id, display: e.name, type: 'entity' as const }))
      this.allCompanyOptions = [...groupOptions, ...entityOptions]
    })
  }

  private getSelectedCompanies() {
    this.relatedCompaniesSubscription$ = this.initializeService.getSelectedCompanies().subscribe(selectedCompanies => {
      if (!selectedCompanies || !selectedCompanies.length) {
        return
      }

      const entityMap = new Map<string, { value: string; display: string }>()

      selectedCompanies.forEach(company => {
        if (company.type === 'ENTITY') {
          entityMap.set(company.id, { value: company.id, display: company.name })
        }
        if (company.relatedEntities?.length) {
          company.relatedEntities.forEach(entity => {
            entityMap.set(entity.id, { value: entity.id, display: entity.name })
          })
        }
      })

      const report = this.reports.find(r => r.filters.some(f => f.label === 'Company Entities list'))
      const filter = report?.filters.find(f => f.label === 'Company Entities list')
      if (filter) {
        filter.options = Array.from(entityMap.values())
      }
    })
  }

  get filteredCompanyOptions() {
    const search = this.companySearchText.toLowerCase().trim()
    if (!search) return this.allCompanyOptions
    return this.allCompanyOptions.filter(o => o.display.toLowerCase().includes(search))
  }

  get selectedCount() {
    return this.selectedCompanyIds.size
  }

  toggleCompanyDropdown(event: Event) {
    event.stopPropagation()
    this.showCompanyDropdown = !this.showCompanyDropdown
    if (!this.showCompanyDropdown) {
      this.companySearchText = ''
    }
  }

  toggleCompany(value: string, event: Event) {
    event.stopPropagation()
    if (this.selectedCompanyIds.has(value)) {
      this.selectedCompanyIds.delete(value)
    } else {
      this.selectedCompanyIds.add(value)
    }
    this.selectedCompanyIds = new Set(this.selectedCompanyIds)
  }

  selectAll(event: Event) {
    event.stopPropagation()
    this.filteredCompanyOptions.forEach(o => this.selectedCompanyIds.add(o.value))
    this.selectedCompanyIds = new Set(this.selectedCompanyIds)
  }

  clearAll(event: Event) {
    event.stopPropagation()
    this.selectedCompanyIds.clear()
    this.selectedCompanyIds = new Set()
  }

  setTab(tab: 'standard' | 'overall') {
    this.activeTab = tab
    this.showCompanyDropdown = false
    this.companySearchText = ''
  }

  onReportChange() {
    this.selectedReport.filters.forEach((filter: { value: null | []; type: string }) => {
      filter.value = filter.type === 'multiselect' ? [] : null
    })
  }

  onOverallReportChange() {
    this.selectedOverallReport.filters.forEach((filter: { value: null; type: string }) => {
      filter.value = null
    })
  }

  downloadReport = () => {
    if (!this.selectedReport) return

    const errorMsg = this.isDateRangeValidFor(this.selectedReport)
    if (errorMsg) {
      this.cs.showError(errorMsg)
      return
    }

    const endPoint = `${ServiceUrlConstants.API_REPORT + this.selectedReport.type}`
    const options = { queryParams: [] }

    this.selectedReport.filters.forEach((filter: { value: any; apiParam: string; type: string }) => {
      if (filter.value) {
        options.queryParams.push({
          key: filter.apiParam,
          value:
            filter.type === 'date'
              ? convertISOToDate(filter.value)
              : filter.type === 'multiselect'
              ? filter.value.map((v: any) => v.value)
              : filter.value
        })
      }
    })

    this.documentService.downloadDocument(endPoint, options)
  }

  downloadOverallReport = () => {
    if (!this.selectedOverallReport) return

    if (!this.selectedCompanyIds.size) {
      this.cs.showError('Please select at least one company group / entity')
      return
    }

    const errorMsg = this.isDateRangeValidFor(this.selectedOverallReport)
    if (errorMsg) {
      this.cs.showError(errorMsg)
      return
    }

    const entityIds: string[] = []
    this.selectedCompanyIds.forEach(id => {
      const group = this.relatedCompanies?.group?.find(g => g.id === id)
      if (group) {
        entityIds.push(...group.relatedEntities.map(e => e.id))
      } else {
        entityIds.push(id)
      }
    })

    const endPoint = `${ServiceUrlConstants.API_REPORT + this.selectedOverallReport.type}`
    const options = { queryParams: [] }

    this.selectedOverallReport.filters.forEach((filter: { value: any; apiParam: string; type: string }) => {
      if (filter.value) {
        options.queryParams.push({
          key: filter.apiParam,
          value: filter.type === 'date' ? convertISOToDate(filter.value) : filter.value
        })
      }
    })

    if (entityIds.length) {
      options.queryParams.push({ key: 'companyEntityIds', value: entityIds })
    }

    this.documentService.downloadDocument(endPoint, options)
  }

  isDateRangeValidFor(report: any): string | null {
    const fromDateFilter = report?.filters.find((filter: { label: string }) => filter.label === 'From')
    const toDateFilter = report?.filters.find((filter: { label: string }) => filter.label === 'To')

    if (fromDateFilter?.value && !toDateFilter?.value) {
      return 'Please select the "To" date.'
    } else if (!fromDateFilter?.value && toDateFilter?.value) {
      return 'Please select the "From" date.'
    } else if (fromDateFilter?.value && toDateFilter?.value) {
      const fromDate = new Date(fromDateFilter.value)
      const toDate = new Date(toDateFilter.value)
      if (fromDate > toDate) {
        return 'The "From" date must be earlier than or equal to the "To" date.'
      }
    }
    return null
  }

  ngOnDestroy() {
    this.relatedCompaniesSubscription$?.unsubscribe()
    this.relatedCompaniesFullSubscription$?.unsubscribe()
    this.enabledModulesSubscription$?.unsubscribe()
  }
}
